import React from "react";
import { useParams } from "react-router-dom";
import { Bucket } from "../../../../api/config/apiConfig";

const QuotationFormDownload = ({ surveyData, formContent }) => {
  const { id, flg } = useParams();
  const isEdit = flg === "1";

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th"; // 11th, 12th, 13th, etc.
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Helper function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    const ordinalSuffix = getOrdinalSuffix(day);

    return `${day}${ordinalSuffix} ${month}, ${year}`;
  };

  return (
    <div className="premium-print-container">
      <div className="section-divider">
        <h1 className="section-title">QUOTATION</h1>
        <div style={{ fontSize: '9pt', fontWeight: 600 }}>
          Ref: {formContent?.invoiceNo || 'QT-' + id}
        </div>
      </div>

      <div className="data-grid">
        <div className="data-item">
          <span className="data-label">To</span>
          <span className="data-value">{formContent?.to || "-"}</span>
        </div>
        <div className="data-item">
          <span className="data-label">Date</span>
          <span className="data-value">
            {formContent?.date ? formatDate(formContent?.date) : "-"}
          </span>
        </div>
        <div className="data-item">
          <span className="data-label">Location</span>
          <span className="data-value">{formContent?.location || "-"}</span>
        </div>
        <div className="data-item">
          <span className="data-label">Validity</span>
          <span className="data-value">{formContent?.quoteValidity || "-"}</span>
        </div>
        <div className="data-item" style={{ gridColumn: 'span 2' }}>
          <span className="data-label">Email</span>
          <span className="data-value" style={{ textDecoration: 'underline' }}>{formContent?.email || "-"}</span>
        </div>
      </div>

      <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #e2e8f0' }}>
        <div className="data-label" style={{ marginBottom: '8px' }}>Dear {formContent?.recipient},</div>
        <div className="p-word" style={{ fontSize: '10pt', lineHeight: 1.6, color: '#334155' }}>
          {formContent?.details || "We are pleased to submit our quotation for your relocation requirements."}
        </div>
      </div>

      <div className="section-divider">
        <h2 className="section-title">Service Details</h2>
      </div>

      <table>
        <tbody>
          {formContent?.description?.trim() && (
            <tr>
              <th style={{ width: '200px' }}>Description</th>
              <td>{formContent.description}</td>
            </tr>
          )}
          {formContent?.serviceRequired?.trim() && (
            <tr>
              <th>Service Required</th>
              <td>{formContent.serviceRequired}</td>
            </tr>
          )}
          {formContent?.packingDate?.trim() && (
            <tr>
              <th>Requested Packing Date</th>
              <td>{formatDate(formContent.packingDate)}</td>
            </tr>
          )}
          {formContent?.originAddress?.trim() && (
            <tr>
              <th>Origin Address</th>
              <td>{formContent.originAddress}</td>
            </tr>
          )}
          {formContent?.destinationAddress?.trim() && (
            <tr>
              <th>Destination Address</th>
              <td>{formContent.destinationAddress}</td>
            </tr>
          )}
          {formContent?.estimatedWeight && (
            <tr>
              <th>Estimated Weight</th>
              <td>{formContent.estimatedWeight} kg</td>
            </tr>
          )}
          {formContent?.estimatedVolume && (
            <tr>
              <th>Estimated Volume</th>
              <td>{(Number(formContent.estimatedVolume)).toFixed(2)} m³</td>
            </tr>
          )}
          {formContent?.transportationMode?.trim() && (
            <tr>
              <th>Transportation Mode</th>
              <td>{formContent.transportationMode}</td>
            </tr>
          )}
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ border: '2px solid #cbd5e1' }}>PROPOSED CHARGES</th>
            <td style={{ border: '2px solid #cbd5e1', fontWeight: 700, fontSize: '12pt', color: '#0f172a' }}>
              {formContent?.charges || "-"}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="section-divider" style={{ marginTop: '20px' }}>
        <h2 className="section-title">Terms & Conditions</h2>
      </div>

      <div className="data-grid" style={{ gridTemplateColumns: '1fr' }}>
        {formContent?.priceIncludeAtOrigin && (
          <div className="data-item">
            <span className="data-label">Price Include at Origin</span>
            <span className="data-value" style={{ fontWeight: 400, fontSize: '9pt' }}>{formContent.priceIncludeAtOrigin}</span>
          </div>
        )}
        {formContent?.standardExclusions && (
          <div className="data-item">
            <span className="data-label">Standard Exclusions</span>
            <span className="data-value" style={{ fontWeight: 400, fontSize: '9pt' }}>{formContent.standardExclusions}</span>
          </div>
        )}
        {formContent?.insurance && (
          <div className="data-item">
            <span className="data-label">Insurance</span>
            <span className="data-value" style={{ fontWeight: 400, fontSize: '9pt' }}>{formContent.insurance}</span>
          </div>
        )}
        {formContent?.documentsRequired && (
          <div className="data-item">
            <span className="data-label">Documents Required</span>
            <span className="data-value" style={{ fontWeight: 400, fontSize: '9pt' }}>{formContent.documentsRequired}</span>
          </div>
        )}
        {formContent?.paymentTerms && (
          <div className="data-item">
            <span className="data-label">Payment Terms</span>
            <span className="data-value" style={{ fontWeight: 400, fontSize: '9pt' }}>{formContent.paymentTerms}</span>
          </div>
        )}
      </div>

      <div className="section-divider" style={{ marginTop: '20px' }}>
        <h2 className="section-title">Bank Details</h2>
      </div>

      <div className="data-grid">
        <div className="data-item">
          <span className="data-label">Account Title</span>
          <span className="data-value">{formContent?.bankDetails?.title || "-"}</span>
        </div>
        <div className="data-item">
          <span className="data-label">IBAN</span>
          <span className="data-value">{formContent?.bankDetails?.iban || "-"}</span>
        </div>
        <div className="data-item">
          <span className="data-label">Swift Code</span>
          <span className="data-value">{formContent?.bankDetails?.swiftCode || "-"}</span>
        </div>
        <div className="data-item">
          <span className="data-label">Branch Details</span>
          <span className="data-value">{formContent?.bankDetails?.branchNameCode || "-"}</span>
        </div>
      </div>

      {!isEdit && (
        <div style={{ display: 'flex', marginTop: '20px', gap: '40px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ height: '80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: '10px' }}>
              {formContent.clientSign && (
                <img src={`${Bucket}${formContent.clientSign}`} height={80} style={{ objectFit: 'contain' }} />
              )}
            </div>
            <div style={{ paddingTop: '5px', fontWeight: 700 }}>QUOTATION Acceptance</div>
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ height: '80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginBottom: '10px' }}>
              {formContent.adminSign && (
                <img src={`${Bucket}${formContent.adminSign}`} height={80} style={{ objectFit: 'contain' }} />
              )}
            </div>
            <div style={{ paddingTop: '5px', fontWeight: 700 }}>Shelozon Movers Packers & Logistics</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationFormDownload;
